/**
 * Harte Plattform-Regeln von TikTok, Instagram und Facebook, an einer Stelle.
 * Quellen:
 *  - TikTok Content Posting API (video/init, content/init, status/fetch)
 *  - Instagram Platform Content Publishing (media, media_publish)
 *  - Facebook Pages API (video_reels, photos, feed)
 */
export const TIKTOK = {
    authUrl: 'https://www.tiktok.com/v2/auth/authorize/',
    apiBase: 'https://open.tiktokapis.com',
    /**
     * Was wir bei der Anmeldung anfordern. TikTok bricht die Autorisierung ab,
     * sobald ein Scope dabei ist, den die App nicht bewilligt bekommen hat —
     * und ohne bestandenes Audit bewilligt TikTok in aller Regel nur diese
     * beiden. Weitere über TIKTOK_SCOPES in der .env freischalten, sobald sie
     * in der Developer-Konsole tatsächlich genehmigt sind.
     */
    defaultScopes: ['user.info.basic', 'video.upload'],
    /** Alles, was die App nutzen kann, wenn TikTok es genehmigt hat. */
    knownScopes: ['user.info.basic', 'user.info.profile', 'user.info.stats', 'video.publish', 'video.upload', 'video.list'],
    /** Direktes Posten braucht video.publish, Draft-in-die-Inbox nur video.upload */
    endpoints: {
        token: '/v2/oauth/token/',
        revoke: '/v2/oauth/revoke/',
        userInfo: '/v2/user/info/',
        creatorInfo: '/v2/post/publish/creator_info/query/',
        videoInit: '/v2/post/publish/video/init/',
        contentInit: '/v2/post/publish/content/init/',
        inboxVideoInit: '/v2/post/publish/inbox/video/init/',
        statusFetch: '/v2/post/publish/status/fetch/',
    },
    privacyLevels: ['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'FOLLOWER_OF_CREATOR', 'SELF_ONLY'],
    limits: {
        /** Init-Aufrufe pro Minute und Access-Token */
        initPerMinute: 6,
        /** Offene, noch nicht in der App bestätigte Shares pro 24 h */
        pendingSharesPer24h: 5,
        /** Titel über einer Fotoserie */
        titleMaxRunes: 90,
        /** Beschreibung unter einer Fotoserie */
        descriptionMaxRunes: 4000,
        /** Die Caption eines Videos heißt bei TikTok `title` und endet bei 2200 */
        videoCaptionMaxRunes: 2200,
        /** Fotos: nur JPEG oder WebP, höchstens 1080p und 20 MB je Bild */
        photoMaxBytes: 20 * 1024 * 1024,
        /** Videos über die Schnittstelle: höchstens 4 GB */
        videoMaxBytes: 4 * 1024 * 1024 * 1024,
        photoMaxCount: 35,
        /** Mindest-Chunk bei FILE_UPLOAD */
        minChunkBytes: 5 * 1024 * 1024,
        maxChunkBytes: 64 * 1024 * 1024,
        /** Dateien darunter werden in einem Stück hochgeladen */
        singleChunkMaxBytes: 64 * 1024 * 1024,
        maxChunkCount: 1000,
    },
};
export const INSTAGRAM = {
    authUrl: 'https://www.facebook.com/{version}/dialog/oauth',
    graphBase: 'https://graph.facebook.com',
    /** Instagram Login: Anmeldung direkt bei Instagram, ohne Facebook-Seite */
    loginAuthUrl: 'https://www.instagram.com/oauth/authorize',
    loginTokenUrl: 'https://api.instagram.com/oauth/access_token',
    loginGraphBase: 'https://graph.instagram.com',
    loginScopes: [
        'instagram_business_basic',
        'instagram_business_content_publish',
        'instagram_business_manage_insights',
    ],
    defaultVersion: 'v23.0',
    scopes: [
        'instagram_basic',
        'instagram_content_publish',
        'instagram_manage_insights',
        'pages_show_list',
        'pages_read_engagement',
        'business_management',
    ],
    containerStates: ['EXPIRED', 'ERROR', 'FINISHED', 'IN_PROGRESS', 'PUBLISHED'],
    limits: {
        /** API-veröffentlichte Posts je Account in 24 h */
        postsPer24h: 100,
        carouselMaxChildren: 10,
        captionMaxChars: 2200,
        hashtagMaxCount: 30,
        reelMaxSeconds: 900,
        reelMinSeconds: 3,
        /** Story-Videos deckelt Instagram bei 60 s, längere Container enden in ERROR */
        storyMaxSeconds: 60,
        /** Dateigrößen laut IG-User-Media-Referenz */
        reelMaxBytes: 300 * 1024 * 1024,
        storyVideoMaxBytes: 100 * 1024 * 1024,
        imageMaxBytes: 8 * 1024 * 1024,
    },
};
/**
 * Facebook-Seiten. Gepostet wird mit dem Seiten-Token, der beim Verbinden
 * von Instagram ohnehin anfällt -- die Seite ist dieselbe, an der das
 * Instagram-Konto hängt. Zusätzlich nötig ist die Berechtigung
 * `pages_manage_posts`.
 */
export const FACEBOOK = {
    uploadBase: 'https://rupload.facebook.com/video-upload',
    publishScope: 'pages_manage_posts',
    limits: {
        /** Reels über die Schnittstelle je Seite in 24 h */
        reelsPer24h: 30,
        reelMinSeconds: 3,
        reelMaxSeconds: 90,
        /** Ein Foto darf höchstens 4 MB groß sein */
        photoMaxBytes: 4 * 1024 * 1024,
        /** Keine dokumentierte Obergrenze; zehn hält die Serie mit Instagram gleich */
        photoMaxCount: 10,
        messageMaxChars: 5000,
    },
};
/** Videoformat, das beide Plattformen ohne Nachbearbeitung akzeptieren. */
export const RENDER_PRESET = {
    width: 1080,
    height: 1920,
    fps: 30,
    videoCodec: 'libx264',
    audioCodec: 'aac',
    audioBitrate: '128k',
    pixelFormat: 'yuv420p',
    crf: 21,
    preset: 'veryfast',
    /** TikTok erwartet moov-Atom am Anfang */
    faststart: true,
};
/**
 * TikTok erlaubt PULL_FROM_URL nur von verifizierten Domains. Solange die
 * Domain nicht verifiziert ist, muss über FILE_UPLOAD gechunked werden.
 */
export function tiktokChunkPlan(sizeBytes) {
    const { minChunkBytes, singleChunkMaxBytes } = TIKTOK.limits;
    if (sizeBytes <= singleChunkMaxBytes)
        return { chunkSize: sizeBytes, totalChunks: 1 };
    /*
     * TikTok rechnet abgerundet: total_chunk_count = floor(Größe / Blockgröße),
     * und der LETZTE Block nimmt den Rest mit (er darf bis 128 MB wachsen).
     * Ein kleinerer Restblock am Ende ist nicht erlaubt. Mit zehn Megabyte je
     * Block bleibt der letzte immer unter 20 MB.
     */
    const chunkSize = Math.max(minChunkBytes, 10 * 1024 * 1024);
    return { chunkSize, totalChunks: Math.floor(sizeBytes / chunkSize) };
}
/**
 * Welche Felder /v2/user/info/ liefern darf, hängt an den tatsächlich
 * erteilten Scopes. Fragt man ein Feld ohne den passenden Scope ab,
 * antwortet TikTok mit einem Fehler, statt das Feld einfach wegzulassen —
 * die Verbindung würde also nach erfolgreichem Login scheitern.
 */
export function tiktokUserFields(granted) {
    const fields = ['open_id', 'union_id', 'avatar_url', 'display_name'];
    if (granted.includes('user.info.profile'))
        fields.push('username');
    if (granted.includes('user.info.stats'))
        fields.push('follower_count', 'likes_count', 'video_count');
    return fields;
}
/* ------------------------------------------------- Ziele und ihre Gestalt */
/**
 * Alle Veröffentlichungsziele in der Reihenfolge, in der sie zur Auswahl
 * stehen. Eine Liste, damit Oberfläche, Zod-Schemas und Mongoose-Enums nicht
 * jeweils ihre eigene führen.
 */
export const PUBLISH_TARGETS = ['tiktok', 'tiktok_photo', 'ig_reels', 'ig_feed', 'ig_story', 'fb_reels', 'fb_feed'];
/**
 * Ziele, für die ein fertiges MP4 gebraucht wird. Alles andere kommt mit den
 * Einzelbildern aus — und spart sich das Rendern.
 */
const VIDEO_TARGETS = new Set(['tiktok', 'ig_reels', 'ig_story', 'fb_reels']);
const PHOTO_TARGETS = new Set(['tiktok_photo', 'ig_feed', 'fb_feed']);
/** Zu welchem Netzwerk ein Ziel gehört. */
export function targetPlatform(target) {
    if (target === 'tiktok' || target === 'tiktok_photo')
        return 'tiktok';
    return target.startsWith('fb_') ? 'facebook' : 'instagram';
}
export function isFacebookTarget(target) {
    return targetPlatform(target) === 'facebook';
}
/**
 * Ziele, die im Feed als 4:5-Hochformat stehen. Instagram verlangt es
 * (9:16 lehnt der Feed ab), und im Facebook-Feed nimmt dasselbe Format
 * den meisten Platz ein.
 */
export function usesFeedCrop(target) {
    return target === 'ig_feed' || target === 'fb_feed';
}
export function needsFeedCrop(targets) {
    return (targets ?? []).some(usesFeedCrop);
}
/** Ob die Seite hinter dem Instagram-Konto bespielt werden darf. */
export function facebookCanPublish(account) {
    return !!account?.metaPageId && (account.scopes ?? []).includes(FACEBOOK.publishScope);
}
/** Wie das Ziel in Meldungen heißt. */
export function targetLabel(target) {
    return {
        tiktok: 'TikTok Video', tiktok_photo: 'TikTok Fotos', ig_reels: 'Instagram Reel', ig_feed: 'Instagram Feed',
        ig_story: 'Instagram Story', fb_reels: 'Facebook Reel', fb_feed: 'Facebook Beitrag',
    }[target] ?? target;
}
export function isPhotoTarget(target) {
    return PHOTO_TARGETS.has(target);
}
/*
 * Der Gegenpol sind `tiktok_photo` und `ig_feed`: Bildserien zum
 * Durchwischen, bei denen jedes Bild ein Bild bleibt und der Zuschauer das
 * Tempo bestimmt. Sie brauchen kein Video -- mehr muss der Code darueber
 * nicht wissen, deshalb steht hier nur die eine Liste.
 */
export function targetNeedsVideo(target) {
    return VIDEO_TARGETS.has(target);
}
/**
 * Braucht dieser Post überhaupt ein Video?
 *
 * Ohne Ziele gibt es keinen Anlass, ein Video zu erzeugen. Die Freigabe
 * verlangt ohnehin eine ausdrückliche Zielwahl.
 */
export function needsVideoRender(targets) {
    return (targets ?? []).some(targetNeedsVideo);
}
/** Ziele für den Projektstart; bei „Gemischt“ trennt der Agent sie je Post. */
export function targetsForFormat(format, platforms) {
    const targets = [];
    for (const platform of new Set(platforms)) {
        if (platform === 'tiktok') {
            if (format !== 'video')
                targets.push('tiktok_photo');
            if (format !== 'images')
                targets.push('tiktok');
        }
        else if (platform === 'facebook') {
            if (format !== 'video')
                targets.push('fb_feed');
            if (format !== 'images')
                targets.push('fb_reels');
        }
        else {
            if (format !== 'video')
                targets.push('ig_feed');
            if (format !== 'images')
                targets.push('ig_reels');
        }
    }
    return targets;
}
/** Ein automatischer Post geht nur an Ziele seines tatsächlichen Formats. */
/*
 * Jeder Post geht auf JEDES gewählte Netzwerk, im passenden Format. Früher
 * wurde nur gefiltert: Stand bei Instagram nur „Feed" in der Liste, bekam ein
 * Video-Post gar kein Instagram, und eine Bildserie ohne „TikTok-Foto" kein
 * TikTok. Jetzt wird das Ziel ins Gegenstück übersetzt.
 */
const AS_VIDEO = { tiktok_photo: 'tiktok', ig_feed: 'ig_reels', fb_feed: 'fb_reels' };
const AS_PHOTO = { tiktok: 'tiktok_photo', ig_reels: 'ig_feed', fb_reels: 'fb_feed' };
export function targetsForPostKind(targets, kind, 
/**
 * Bildserien zusätzlich als Reel auf Instagram und Facebook: dieselben
 * Folien als animiertes Video (Kamerafahrt, einfliegende Karten, Musik).
 * Das Karussell erreicht die Follower, das Reel neue Leute. TikTok bleibt
 * beim Fotomodus, der dort selbst stark ausgespielt wird.
 */
reelFromImages = false) {
    const out = new Set();
    for (const t of targets) {
        if (kind === 'video') {
            if (targetNeedsVideo(t))
                out.add(t);
            else if (AS_VIDEO[t])
                out.add(AS_VIDEO[t]);
        }
        else if (isPhotoTarget(t))
            out.add(t);
        else if (t !== 'ig_story' && AS_PHOTO[t])
            out.add(AS_PHOTO[t]);
    }
    if (kind === 'image' && reelFromImages) {
        for (const t of [...out]) {
            if (t === 'ig_feed')
                out.add('ig_reels');
            if (t === 'fb_feed')
                out.add('fb_reels');
        }
    }
    return [...out];
}
/** Projektziele gelten für verschiedene Posts, daher dürfen beide TikTok-Formate aktiv sein. */
export function toggleAutomationTarget(targets, key) {
    return targets.includes(key) ? targets.filter((target) => target !== key) : [...targets, key];
}
/** Beide TikTok-Ziele hängen am selben Konto und denselben Einstellungen. */
export function isTiktokTarget(target) {
    return target === 'tiktok' || target === 'tiktok_photo';
}
export function hasTiktokTarget(targets) {
    return (targets ?? []).some(isTiktokTarget);
}
/**
 * Wie viele Bilder das Ziel höchstens annimmt. TikTok deckelt bei 35,
 * Instagram bei 10 Karussell-Kindern.
 */
export function photoLimitFor(target) {
    if (target === 'tiktok_photo')
        return TIKTOK.limits.photoMaxCount;
    return target === 'fb_feed' ? FACEBOOK.limits.photoMaxCount : INSTAGRAM.limits.carouselMaxChildren;
}
/**
 * Wie lang ein Video für dieses Ziel sein darf, in Sekunden. TikTok nennt
 * die Obergrenze je Konto erst über creator_info, deshalb steht sie hier nicht.
 */
export function videoSecondsFor(target) {
    if (target === 'ig_reels')
        return { min: INSTAGRAM.limits.reelMinSeconds, max: INSTAGRAM.limits.reelMaxSeconds };
    if (target === 'ig_story')
        return { min: INSTAGRAM.limits.reelMinSeconds, max: INSTAGRAM.limits.storyMaxSeconds };
    if (target === 'fb_reels')
        return { min: FACEBOOK.limits.reelMinSeconds, max: FACEBOOK.limits.reelMaxSeconds };
    return null;
}
/** Ein Feed-Bild bleibt ein Einzelbild; ab zwei Bildern entsteht ein Karussell. */
export function instagramFeedMediaKind(imageCount) {
    if (!Number.isInteger(imageCount) || imageCount < 1 || imageCount > INSTAGRAM.limits.carouselMaxChildren) {
        throw new Error(`Instagram Feed braucht 1 bis ${INSTAGRAM.limits.carouselMaxChildren} Bilder.`);
    }
    return imageCount === 1 ? 'image' : 'carousel';
}
/**
 * Ein Ziel an- oder abwählen, mit der einen Regel, die dabei gilt.
 *
 * TikTok Video und TikTok Fotos schließen einander aus: Derselbe Post
 * zweimal auf demselben Konto, einmal als Clip und einmal als Bildserie,
 * ist kein Wunsch, sondern ein Versehen. Bei Instagram gilt das nicht --
 * Reel und Feed-Karussell sind dort zwei Plätze, die man bewusst beide
 * bespielen kann.
 */
export function toggleTarget(targets, key) {
    if (targets.includes(key))
        return targets.filter((t) => t !== key);
    const gegenstueck = key === 'tiktok' ? 'tiktok_photo' : key === 'tiktok_photo' ? 'tiktok' : null;
    return [...targets.filter((t) => t !== gegenstueck), key];
}
/** Direct Post ist ohne video.publish technisch unmöglich. */
export function tiktokCanDirectPost(granted) {
    return granted.includes('video.publish');
}
/** Zählt UTF-16-Einheiten, so wie TikTok das Limit definiert. */
export function utf16Length(s) {
    return s.length;
}
/** Wie lang der Begleittext je Ziel sein darf. */
export function captionLimitFor(target) {
    if (target === 'tiktok')
        return TIKTOK.limits.videoCaptionMaxRunes;
    if (target === 'tiktok_photo')
        return TIKTOK.limits.descriptionMaxRunes;
    return isFacebookTarget(target) ? FACEBOOK.limits.messageMaxChars : INSTAGRAM.limits.captionMaxChars;
}
/** Kürzt auf das Limit, ohne ein Emoji oder Sonderzeichen zu zerteilen. */
export function clampText(text, max) {
    if (text.length <= max)
        return text;
    let cut = text.slice(0, max - 1);
    const last = cut.charCodeAt(cut.length - 1);
    if (last >= 0xd800 && last <= 0xdbff)
        cut = cut.slice(0, -1);
    return cut + '…';
}
export function clampCaption(text, platform) {
    return clampText(text, platform === 'tiktok' ? TIKTOK.limits.videoCaptionMaxRunes : INSTAGRAM.limits.captionMaxChars);
}
//# sourceMappingURL=platform.js.map